import { Box, Typography, MenuItem, Select } from "@mui/material";
import dayjs from "dayjs";
import React, { useState, useEffect } from "react";

const CustomDateInput = ({
  title,
  name,
  handleChange,
  value,
  errors: propErrors,
  dataType,
  disable,
  noMaxDate,
}) => {
  const [month, setMonth] = useState("");
  const [year, setYear] = useState("");
  const [localErrors, setLocalErrors] = useState(null);

  const generateFinancialYears = () => {
    const currentDate = dayjs();
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month(); // 0-based (0 = January)

    // Determine last completed financial year
    const lastCompletedFYStart =
      currentMonth >= 3 ? currentYear - 1 : currentYear - 2;

    const years = [];
    // Generate last 10 completed financial years
    for (let i = 0; i < 10; i++) {
      const startYear = lastCompletedFYStart - i;
      const endYear = startYear + 1;
      years.push(`${startYear}-${endYear.toString().slice(-2)}`);
    }
    return years;
  };

  // Generate months
  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  // Generate years for Production
  const generateYears = () => {
    const currentDate = dayjs();
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month() + 1; // dayjs months are 0-based

    let maxYear = currentYear;

    // Check if a month is selected and adjust maxYear if it's a future month
    if (month) {
      const selectedMonth = parseInt(month, 10);
      if (selectedMonth > currentMonth) {
        maxYear = currentYear - 1;
      }
    }

    const years = [];
    for (let i = 0; i < 10; i++) {
      years.push(maxYear - i);
    }
    return years;
  };

  useEffect(() => {
    if (value) {
      if (dataType === "Production") {
        // Handle "MM-YYYY" format for Production
        const [monthPart, yearPart] = value.split("-");
        if (monthPart && yearPart) {
          setMonth(monthPart.padStart(2, "0"));
          setYear(yearPart);
        }
      } else {
        // Handle "YYYY-YY" format for Revenue
        setYear(value);
      }
    } else {
      // Reset state when no value
      setMonth("");
      setYear("");
    }
  }, [value, dataType]);

  const handleProductionChange = (type, val) => {
    const newMonth = type === "month" ? val : month;
    const newYear = type === "year" ? val : year;

    // Update local state immediately
    if (type === "month") setMonth(newMonth);
    if (type === "year") setYear(newYear);

    // Only update parent when both values are present
    if (newMonth && newYear) {
      const formattedDate = dayjs(`${newYear}-${newMonth}-01`).format(
        "MM-YYYY"
      );
      handleChange({ name, value: formattedDate });
    }
  };

  const handleRevenueChange = (financialYear) => {
    setYear(financialYear);
    // Directly pass the financial year format to parent
    handleChange({ name, value: financialYear });
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
        {dataType === "Production" ? "Month of Production" : "Financial Year"}
      </Typography>

      {dataType === "Production" ? (
        <Box display="flex" gap={2}>
          <Select
            value={month}
            onChange={(e) => handleProductionChange("month", e.target.value)}
            disabled={disable}
            displayEmpty
            sx={selectStyle}
          >
            <MenuItem value="" disabled>
              Month
            </MenuItem>
            {months.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </Select>

          <Select
            value={year}
            onChange={(e) => handleProductionChange("year", e.target.value)}
            disabled={disable}
            displayEmpty
            sx={selectStyle}
          >
            <MenuItem value="" disabled>
              Year
            </MenuItem>
            {generateYears().map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </Box>
      ) : (
        <Select
          value={year}
          onChange={(e) => handleRevenueChange(e.target.value)}
          disabled={disable}
          displayEmpty
          sx={selectStyle}
        >
          <MenuItem value="" disabled>
            Select Year
          </MenuItem>
          {generateFinancialYears().map((fy) => (
            <MenuItem key={fy} value={fy}>
              {fy}
            </MenuItem>
          ))}
        </Select>
      )}

      {propErrors && (
        <Typography variant="body2" color="#D34040">
          {propErrors}
        </Typography>
      )}
    </Box>
  );
};

const selectStyle = {
  width: "100%",
  height: "40px",
  backgroundColor: "#FFFFFF",
  border: "1px solid #B8B8B8",
  borderRadius: "5px",
  "&:hover": {
    border: "1px solid #B8B8B8",
  },
  "& .MuiSelect-select": {
    padding: "10px",
  },
};

export default CustomDateInput;
